import React, { FunctionComponent, useState } from "react";
import Box from "../../styles/Box";
import { Form, Field, FormSpy } from "react-final-form";
import { SearchParams } from "../../services/mobile";
import styled from "styled-components";
import theme from "../../styles/theme";
import { ReactComponent as CheckDark } from "../../assets/svg/check-dark.svg";
import { ReactComponent as CheckLight } from "../../assets/svg/check-light.svg";
import { ReactComponent as LogoLight } from "../../assets/svg/logo-light.svg";
import { ReactComponent as CloseIcon } from "../../assets/svg/close.svg";
import Select from "../../styles/Select";
import H3 from "../../styles/H3";
import Input from "../../styles/Input";
import InputCheckbox from "../../styles/InputCheckbox";
import CheckboxList from "../../styles/CheckboxList";
import BoxHighlight from "../../styles/BoxHighlight";

import { SearchState } from "../../pages/Search/Search";

const constructionYears = [
  new Date().getFullYear() - 4,
  new Date().getFullYear() - 3,
  new Date().getFullYear() - 2,
  new Date().getFullYear() - 1,
  new Date().getFullYear(),
];

const mileages = [
  1500, 5000, 10000, 15000, 20000, 25000, 30000, 35000, 40000, 45000, 50000,
  55000, 60000, 65000, 70000, 75000, 80000, 85000,
];

const ColorWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

interface ColorInputWrapperProps {
  borderColor: string;
  backgroundColor: string;
}
const colorDimensions = 20;
const ColorInputWrapper = styled.div<ColorInputWrapperProps>`
  &:last-child {
    margin-right: 0;
  }

  position: relative;
  margin: 0 ${theme.unit / 2}px ${theme.unit * 2}px 0;
  display: inline;
  height: ${colorDimensions + 1}px;

  input {
    appearance: none;
    position: absolute;
    border: 0;
    width: 100%;
    height: 100%;

    &:focus {
      outline: 0;
    }
  }
  label {
    width: ${colorDimensions}px;
    height: ${colorDimensions}px;
    border-radius: ${colorDimensions / 2 + 1}px;
    display: block;
    border: 1px solid ${(props) => props.borderColor};
    background-color: ${(props) => props.backgroundColor};
    span {
      display: none;
    }
  }

  svg {
    position: absolute;
    top: 7px;
    left: 5px;
  }
`;

const ResultWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  svg {
    display: none;
  }

  margin-bottom: ${theme.unit * 2}px;
  div {
    color: ${theme.colors.white};
    font-size: ${theme.fonts.big.fontSize};
  }

  @media (min-width: ${theme.mediaQueries.tablet}px) {
    justify-content: center;
    align-items: center;
    svg {
      display: initial;
    }

    div {
      margin-left: ${theme.unit}px;
    }
  }
`;

interface FilterBoxProps {
  hideForMobile: boolean;
}

const FilterBox = styled(Box)<FilterBoxProps>`
  @media (max-width: ${theme.mediaQueries.tablet}px) {
    display: ${(props) => (props.hideForMobile ? "none" : "flex")};
    position: fixed;
    flex-direction: column;
    max-height: 100%;
    top: 0;
    left: 0;
    width: 100%;
    background-color: ${theme.colors.white};
    z-index: 10000;
    padding-top: 0;
    padding-bottom: 0;
  }

  @media (min-width: ${theme.mediaQueries.tablet}px) {
    display: block;
    position: static;
  }
`;

const FilterToggle = styled.a`
  margin-left: auto;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
  @media (min-width: ${theme.mediaQueries.tablet}px) {
    display: none;
  }
`;

const FilterBoxHeader = styled.div`
  height: ${theme.unit * 4}px;
  flex-shrink: 0;
  font-size: ${theme.fonts.big.fontSize};
  font-weight: ${theme.fonts.big.fontWeight};
  border-bottom: 1px solid ${theme.colors.lightGrey};
  margin-bottom: ${theme.unit}px;
  margin-left: ${-1 * theme.unit}px;
  margin-right: ${-1 * theme.unit}px;

  padding-left: ${theme.unit}px;
  padding-right: ${theme.unit}px;
  flex: none;

  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
  }
  @media (min-width: ${theme.mediaQueries.tablet}px) {
    display: none;
  }
`;

const FilterBoxContent = styled.div`
  @media (max-width: ${theme.mediaQueries.tablet}px) {
    display: flex;
    flex-direction: column;
    flex: auto;
    overflow: scroll;
    margin-left: ${-1 * theme.unit}px;
    margin-right: ${-1 * theme.unit}px;
    padding-left: ${theme.unit}px;
    padding-right: ${theme.unit}px;
  }
`;

const FilterBoxFooter = styled.div`
  height: ${theme.unit * 4}px;
  flex-shrink: 0;
  font-size: ${theme.fonts.medium.fontSize};
  margin-left: ${-1 * theme.unit}px;
  margin-right: ${-1 * theme.unit}px;
  padding-left: ${theme.unit}px;
  padding-right: ${theme.unit}px;
  border-top: 1px solid ${theme.colors.lightGrey};

  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 100%;
  }
  @media (min-width: ${theme.mediaQueries.tablet}px) {
    display: none;
  }
`;

const CloseFilterButton = styled.button`
  margin-left: auto;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const FilterButton = styled.button`
  padding: ${theme.unit * 0.5}px ${theme.unit * 2}px;
  background-color: ${theme.colors.pink};
  border: 0;
  color: ${theme.colors.white};
`;

interface Props {
  searchState: SearchState;
  onSubmit: (values: SearchParams) => void;
  setBrandRef: (value: string) => void;
}

const SearchForm: FunctionComponent<Props> = ({
  searchState,
  onSubmit,
  setBrandRef,
}) => {
  const {
    brands,
    models,
    pagination,
    searchParams,
    fuelTypes,
    interiorColors,
    interiorTypes,
    features,
    categories,
  } = searchState;
  const [hideFiltersMobile, setHideFiltersMobile] = useState(true);
  return (
    <Form
      initialValues={{
        page: 1,
        minConstructionYear: constructionYears[0],
        maxConstructionYear: constructionYears[constructionYears.length - 1],
        minMileage: 1500,
        maxMileage: 85000,
        ...searchParams,
      }}
      onSubmit={onSubmit}
      render={({ handleSubmit, values }) => (
        <form method="GET" onSubmit={handleSubmit}>
          <BoxHighlight>
            <ResultWrapper>
              <LogoLight />
              {pagination && (
                <div>
                  {Intl.NumberFormat("nl-NL", {}).format(pagination.total)}{" "}
                  {pagination.total === 1 ? "resultaat" : "resultaten"}
                </div>
              )}
              <FilterToggle
                onClick={(e) => {
                  e.preventDefault();
                  setHideFiltersMobile(!hideFiltersMobile);
                }}
              >
                filters
              </FilterToggle>
            </ResultWrapper>
            <div>
              <Field name="brandRef" component={Select}>
                <option value="">--Kies een merk--</option>
                {brands &&
                  brands.map((brand) => (
                    <option key={brand.ref} value={brand.ref}>
                      {brand.name}
                    </option>
                  ))}
              </Field>
            </div>
            <div>
              <Field name="modelRef" component={Select}>
                <option value="">--Kies een model--</option>
                {values.brandRef &&
                  models &&
                  models.map((model) => (
                    <option key={model.ref} value={model.ref}>
                      {model.name}
                    </option>
                  ))}
              </Field>
            </div>
          </BoxHighlight>
          <FilterBox hideForMobile={hideFiltersMobile}>
            <FilterBoxHeader>
              <div>
                <span>
                  Verfijn zoekresultaat
                  {pagination && <span> ({pagination.total})</span>}
                </span>
                <CloseFilterButton onClick={() => setHideFiltersMobile(true)}>
                  <CloseIcon />
                </CloseFilterButton>
              </div>
            </FilterBoxHeader>
            <FilterBoxContent>
              <div>
                <div>
                  <H3>Prijs van - tot</H3>
                  <div>
                    <Field
                      name="priceMin"
                      component={Input}
                      type="number"
                      placeholder="€6.594"
                      size="small"
                    />{" "}
                    tot{" "}
                    <Field
                      name="priceMax"
                      component={Input}
                      type="number"
                      placeholder="€22.000"
                      size="small"
                    />
                  </div>
                </div>
                <div>
                  <H3>Kleur</H3>
                  <ColorWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.black}
                      backgroundColor={theme.colors.black}
                    >
                      <Field
                        id="color-BLACK"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="BLACK"
                      />
                      <label htmlFor="color-BLACK">
                        <span>Zwart</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("BLACK") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.grey}
                      backgroundColor={theme.colors.grey}
                    >
                      <Field
                        id="color-GREY"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="GREY"
                      />
                      <label htmlFor="color-GREY">
                        <span>Grijs</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("GREY") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.beige}
                      backgroundColor={theme.colors.beige}
                    >
                      <Field
                        id="color-BEIGE"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="BEIGE"
                      />
                      <label htmlFor="color-BEIGE">
                        <span>Beige</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("BEIGE") !== -1 && (
                          <CheckDark style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.brown}
                      backgroundColor={theme.colors.brown}
                    >
                      <Field
                        id="color-BROWN"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="BROWN"
                      />
                      <label htmlFor="color-BROWN">
                        <span>Bruin</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("BROWN") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.red}
                      backgroundColor={theme.colors.red}
                    >
                      <Field
                        id="color-RED"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="RED"
                      />
                      <label htmlFor="color-RED">
                        <span>Red</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("RED") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.green}
                      backgroundColor={theme.colors.green}
                    >
                      <Field
                        id="color-GREEN"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="GREEN"
                      />
                      <label htmlFor="color-GREEN">
                        <span>Groen</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("GREEN") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.blue}
                      backgroundColor={theme.colors.blue}
                    >
                      <Field
                        id="color-BLUE"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="BLUE"
                      />
                      <label htmlFor="color-BLUE">
                        <span>Blauw</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("BLUE") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.purple}
                      backgroundColor={theme.colors.purple}
                    >
                      <Field
                        id="color-PURPLE"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="PURPLE"
                      />
                      <label htmlFor="color-PURPLE">
                        <span>Paars</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("PURPLE") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.gold}
                      backgroundColor={theme.colors.gold}
                    >
                      <Field
                        id="color-GOLD"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="GOLD"
                      />
                      <label htmlFor="color-GOLD">
                        <span>Goud</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("GOLD") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.grey}
                      backgroundColor={theme.colors.white}
                    >
                      <Field
                        id="color-WHITE"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="WHITE"
                      />
                      <label htmlFor="color-WHITE">
                        <span>Wit</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("WHITE") !== -1 && (
                          <CheckDark style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.orange}
                      backgroundColor={theme.colors.orange}
                    >
                      <Field
                        id="color-ORANGE"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="ORANGE"
                      />
                      <label htmlFor="color-ORANGE">
                        <span>Oranje</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("ORANGE") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.silver}
                      backgroundColor={theme.colors.silver}
                    >
                      <Field
                        id="color-SILVER"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="SILVER"
                      />
                      <label htmlFor="color-SILVER">
                        <span>Zilver</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("SILVER") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                    <ColorInputWrapper
                      borderColor={theme.colors.yellow}
                      backgroundColor={theme.colors.yellow}
                    >
                      <Field
                        id="color-YELLOW"
                        name="exteriorColor"
                        component="input"
                        type="checkbox"
                        value="YELLOW"
                      />
                      <label htmlFor="color-YELLOW">
                        <span>Geel</span>
                      </label>
                      {values.exteriorColor &&
                        values.exteriorColor.indexOf("YELLOW") !== -1 && (
                          <CheckLight style={{ pointerEvents: "none" }} />
                        )}
                    </ColorInputWrapper>
                  </ColorWrapper>
                </div>
                <div>
                  <H3>Bouwjaar van - tot</H3>
                  <div>
                    <Field
                      name="minConstructionYear"
                      component={Select}
                      size="small"
                    >
                      {constructionYears
                        .filter(
                          (year) =>
                            values.maxConstructionYear &&
                            year <= values.maxConstructionYear
                        )
                        .map((year) => (
                          <option key={year}>{year}</option>
                        ))}
                    </Field>{" "}
                    <span>tot</span>{" "}
                    <Field
                      name="maxConstructionYear"
                      component={Select}
                      size="small"
                    >
                      {constructionYears
                        .filter(
                          (year) =>
                            values.minConstructionYear &&
                            year >= values.minConstructionYear
                        )
                        .map((year) => (
                          <option key={year}>{year}</option>
                        ))}
                    </Field>
                  </div>
                </div>
                <div>
                  <H3>Kilometerstand van - tot</H3>
                  <div>
                    <Field name="minMileage" component={Select} size="small">
                      {mileages
                        .filter(
                          (mileage) =>
                            values.maxMileage && mileage <= values.maxMileage
                        )
                        .map((mileage) => (
                          <option key={mileage} value={mileage}>
                            {new Intl.NumberFormat("nl-NL", {
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            }).format(mileage)}
                          </option>
                        ))}
                    </Field>{" "}
                    <span>tot</span>{" "}
                    <Field name="maxMileage" component={Select} size="small">
                      {mileages
                        .filter(
                          (mileage) =>
                            values.minMileage && mileage >= values.minMileage
                        )
                        .map((mileage) => (
                          <option key={mileage} value={mileage}>
                            {new Intl.NumberFormat("nl-NL", {
                              maximumFractionDigits: 0,
                              minimumFractionDigits: 0,
                            }).format(mileage)}
                          </option>
                        ))}
                    </Field>
                  </div>
                </div>
                <div>
                  <H3>Transmissie</H3>
                  <CheckboxList>
                    <div>
                      <Field
                        id="gearbox-MANUAL_GEAR"
                        name="gearbox"
                        component={InputCheckbox}
                        type="checkbox"
                        value="MANUAL_GEAR"
                      />
                      <label htmlFor="gearbox-MANUAL_GEAR">Handmatig</label>
                    </div>
                    <div>
                      <Field
                        id="gearbox-SEMIAUTOMATIC_GEAR"
                        name="gearbox"
                        component={InputCheckbox}
                        type="checkbox"
                        value="SEMIAUTOMATIC_GEAR"
                      />
                      <label htmlFor="gearbox-SEMIAUTOMATIC_GEAR">
                        Half automatisch
                      </label>
                    </div>
                    <div>
                      <Field
                        id="gearbox-AUTOMATIC_GEAR"
                        name="gearbox"
                        component={InputCheckbox}
                        type="checkbox"
                        value="AUTOMATIC_GEAR"
                      />
                      <label htmlFor="gearbox-AUTOMATIC_GEAR">
                        Automatisch
                      </label>
                    </div>
                  </CheckboxList>
                </div>
                <div>
                  <H3>Brandstof</H3>
                  <CheckboxList>
                    {fuelTypes?.map((item) => (
                      <div>
                        <Field
                          id={`fuelType-${item.key}`}
                          name="fuelType"
                          component={InputCheckbox}
                          type="checkbox"
                          value={item.key}
                        />
                        <label htmlFor={`fuelType-${item.key}`}>
                          {item.name}
                        </label>
                      </div>
                    ))}
                  </CheckboxList>
                </div>
                <div>
                  <H3>Carrosserie</H3>
                  <CheckboxList>
                    {categories?.map((item) => (
                      <div>
                        <Field
                          id={`category-${item.key}`}
                          name="category"
                          component={InputCheckbox}
                          type="checkbox"
                          value={item.key}
                        />
                        <label htmlFor={`category-${item.key}`}>
                          {item.name}
                        </label>
                      </div>
                    ))}
                  </CheckboxList>
                </div>
                <div>
                  <H3>BTW verrekenbaar</H3>
                  <Field name="vatable" component={Select}>
                    <option value="">wel/niet verrekenbaar</option>
                    <option value="1">BTW verrekenbaar</option>
                    <option value="0">BTW niet verrekenbaar</option>
                  </Field>
                </div>
              </div>
              <div>
                <H3>Interieur kleur</H3>
                <CheckboxList>
                  {interiorColors?.map((item) => (
                    <div>
                      <Field
                        id={`interiorColor-${item.key}`}
                        name="interiorColor"
                        component={InputCheckbox}
                        type="checkbox"
                        value={item.key}
                      />
                      <label htmlFor={`interiorColor-${item.key}`}>
                        {item.name}
                      </label>
                    </div>
                  ))}
                </CheckboxList>
              </div>
              <div>
                <H3>Interieur type</H3>
                <CheckboxList>
                  {interiorTypes?.map((item) => (
                    <div>
                      <Field
                        id={`interiorType-${item.key}`}
                        name="interiorType"
                        component={InputCheckbox}
                        type="checkbox"
                        value={item.key}
                      />
                      <label htmlFor={`interiorType-${item.key}`}>
                        {item.name}
                      </label>
                    </div>
                  ))}
                </CheckboxList>
              </div>
              <div>
                <H3>Feature</H3>
                <CheckboxList>
                  {features?.map((item) => (
                    <div>
                      <Field
                        id={`feature-${item.key}`}
                        name="feature"
                        component={InputCheckbox}
                        type="checkbox"
                        value={item.key}
                      />
                      <label htmlFor={`feature-${item.key}`}>{item.name}</label>
                    </div>
                  ))}
                </CheckboxList>
              </div>
            </FilterBoxContent>
            <FilterBoxFooter>
              <div>
                {pagination && (
                  <span>Alle ({pagination.total}) resultaten</span>
                )}
                <FilterButton onClick={() => setHideFiltersMobile(true)}>
                  BEKIJKEN
                </FilterButton>
              </div>
            </FilterBoxFooter>
          </FilterBox>

          <FormSpy
            onChange={({ values }) => {
              setBrandRef(values.brandRef);
              onSubmit({
                brandRef: "",
                modelRef: "",
                ...values,
              });
            }}
          />
        </form>
      )}
    />
  );
};

export default SearchForm;
